import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home", id: "home" },
  { to: "/profile", label: "Profile", id: "profile" },
  { to: "/awards", label: "Honours", id: "awards" },
  { to: "/news", label: "Press", id: "news" },
  { to: "/gallery", label: "Gallery", id: "gallery" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 18);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl py-3 border-b border-[var(--gold-line-soft)]"
          : "bg-transparent py-6 border-b border-transparent"
      }`}
    >
      <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20 grid grid-cols-12 items-center gap-6">
        {/* Brand */}
        <Link
          to="/"
          data-testid="header-brand"
          className="col-span-8 lg:col-span-3 flex flex-col leading-tight"
        >
          <span className="font-marcellus uppercase tracking-[0.5em] text-[0.58rem] text-[var(--gold-deep)]">
            Dr.
          </span>
          <span className="font-couture text-2xl text-[var(--ink)] tracking-tight">
            P. B. Boss
          </span>
        </Link>

        {/* Desktop nav — centered */}
        <nav className="hidden lg:flex col-span-6 items-center justify-center gap-12">
          {links.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.id}-link`}
              className={({ isActive }) =>
                `lux-link font-marcellus text-[0.66rem] uppercase tracking-[0.38em] ${
                  isActive ? "text-[var(--gold-deep)]" : "text-[var(--ink)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Right-side ornament */}
        <div className="hidden lg:flex col-span-3 items-center justify-end gap-3">
          <span className="font-marcellus uppercase tracking-[0.42em] text-[0.62rem] text-[var(--ink-muted)]">
            Est. 1993
          </span>
          <span className="text-[var(--gold)] text-sm">✦</span>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="header-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden col-span-4 justify-self-end w-11 h-11 inline-flex items-center justify-center border border-[var(--gold-line)] text-[var(--gold-deep)] bg-white/80 backdrop-blur"
        >
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden mt-3 mx-8 sm:mx-14 bg-white border-y border-[var(--gold-line)]"
        >
          <div className="px-2 py-8 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${l.id}-link`}
                className={({ isActive }) =>
                  `font-couture text-3xl tracking-tight ${
                    isActive ? "text-[var(--gold-deep)]" : "text-[var(--ink)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
